import {
  CommentRepository,
  createReport,
  deleteReport,
  UserRepository,
} from "@amityco/ts-sdk";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";

import { ColumnModal, Dropdown } from "@/components";
import { DropdownOption } from "@/components/Forms/Dropdown";
import { Delete, Edit, Ellipsis, Flag, UserClose } from "@/icons";
import { useTranslate } from "@/providers/context";

import { CommentItem } from "../../../types";
import { checkReportStatus, isCommunityAdmin } from "../../../util";
import { CommunityCommentModal } from "./CommunityCommentModal";
import { useCommunityError } from "../../../hooks/useCommunityError";

const ConfirmDeleteModal = ({
  commentId,
  open,
  setOpen,
}: {
  commentId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const { translate } = useTranslate();
  const { handleDeletePostError } = useCommunityError();
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDelete = useCallback(async () => {
    if (deleteLoading) {
      return;
    }

    setDeleteLoading(true);

    try {
      await CommentRepository.deleteComment(commentId).catch((e) =>
        handleDeletePostError(e, true),
      );
      toast(
        <div className="flex items-center gap-2">
          <Delete className="h-6 w-6" />
          {`${translate("Comment")} ${translate("Deleted").toLowerCase()}`}
        </div>,
      );
    } catch (e) {
      console.error(e);
    } finally {
      setOpen(false);
      setDeleteLoading(false);
    }
  }, [
    deleteLoading,
    commentId,
    translate,
    setDeleteLoading,
    setOpen,
    handleDeletePostError,
  ]);

  return (
    <ColumnModal
      open={open}
      onOpenChange={setOpen}
      hideTrigger
      cols={3}
      title={translate("Are you sure you want to delete this comment?")}
      primaryText={`${translate("Delete")} ${translate("Comment")}`}
      danger
      confirmIsLoading={deleteLoading}
      confirmDisabled={deleteLoading}
      onConfirm={handleDelete}
      onCancel={() => setOpen(false)}
    >
      <p className="p1-regular p-6 pt-0">
        {translate("Once deleted this action cannot be undone.")}
      </p>
    </ColumnModal>
  );
};

const ConfirmBlockModal = ({
  userId,
  open,
  setOpen,
}: {
  userId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const { translate } = useTranslate();
  const { handleBlockUserError } = useCommunityError();
  const [blockIsLoading, setBlockIsLoading] = useState(false);

  const handleDelete = useCallback(async () => {
    if (blockIsLoading) {
      return;
    }

    setBlockIsLoading(true);

    try {
      await UserRepository.Relationship.blockUser(userId).catch(
        handleBlockUserError,
      );
      toast(
        <div className="flex items-center gap-2">
          <Delete className="h-6 w-6" />
          {translate("User has been blocked")}
        </div>,
      );

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (e) {
      console.error(e);
    } finally {
      setOpen(false);
      setBlockIsLoading(false);
    }
  }, [
    blockIsLoading,
    userId,
    translate,
    setBlockIsLoading,
    setOpen,
    handleBlockUserError,
  ]);

  return (
    <ColumnModal
      open={open}
      onOpenChange={setOpen}
      hideTrigger
      cols={3}
      title={translate("Are you sure you want to block this user?")}
      primaryText={translate("Yes, block user.")}
      danger
      confirmIsLoading={blockIsLoading}
      confirmDisabled={blockIsLoading}
      onConfirm={handleDelete}
      onCancel={() => setOpen(false)}
    >
      <p className="p1-regular p-6 pt-0">
        {translate(
          "You will not be able to see any posts or comments from them in this feed.",
        )}
      </p>
    </ColumnModal>
  );
};

const ConfirmReportModal = ({
  comment,
  open,
  setOpen,
  haveReported,
  setHaveReported,
}: {
  comment: CommentItem;
  open: boolean;
  setOpen: (open: boolean) => void;
  haveReported: boolean;
  setHaveReported: (reported: boolean) => void;
}) => {
  const { translate } = useTranslate();
  const { handleReportError } = useCommunityError();
  const [reportIsLoading, setReportIsLoading] = useState(false);

  const handleReport = useCallback(async () => {
    if (reportIsLoading) {
      return;
    }

    setReportIsLoading(true);

    try {
      if (haveReported) {
        await deleteReport("comment", comment.id).catch((e) =>
          handleReportError(e, true),
        );
        setHaveReported(false);
      } else {
        await createReport("comment", comment.id).catch(handleReportError);
        setHaveReported(true);
      }
      toast(
        translate(
          haveReported ? "Report removed" : "Comment has been reported",
        ),
      );
    } catch (e) {
      console.error(e);
    } finally {
      setOpen(false);
      setReportIsLoading(false);
    }
  }, [
    reportIsLoading,
    comment,
    haveReported,
    setHaveReported,
    translate,
    setReportIsLoading,
    setOpen,
    handleReportError,
  ]);

  return (
    <ColumnModal
      open={open}
      onOpenChange={setOpen}
      hideTrigger
      cols={3}
      title={translate(
        haveReported
          ? "Are you sure you want to remove your report?"
          : "Are you sure you want to report this comment?",
      )}
      primaryText={translate("Yes")}
      danger
      confirmIsLoading={reportIsLoading}
      confirmDisabled={reportIsLoading}
      onConfirm={handleReport}
      onCancel={() => setOpen(false)}
    >
      <div className="h-5" />
    </ColumnModal>
  );
};

export const CommunityCommentDropdown = ({
  comment,
}: {
  comment: CommentItem;
}) => {
  const { translate } = useTranslate();
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isConfirmingBlock, setIsConfirmingBlock] = useState(false);
  const [isConfirmingReport, setIsConfirmingReport] = useState(false);
  const [commentToEdit, setCommentToEdit] = useState<CommentItem>();
  const [haveReported, setHaveReported] = useState(false);
  const [isCheckingReport, setIsCheckingReport] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleDropdownOpen = useCallback(
    (open: boolean) => {
      setDropdownOpen(open);
      if (open) {
        // We can't use isReportedByMe when mapping comments due to rate limiting
        // So instead we only check when the dropdown is opened if there is a flagCount
        if (comment.flagCount === 0 || isCheckingReport) {
          return;
        }
        checkReportStatus(
          "comment",
          comment.id,
          setIsCheckingReport,
          setHaveReported,
        );
      }
    },
    [
      setIsCheckingReport,
      setHaveReported,
      comment.flagCount,
      comment.id,
      isCheckingReport,
    ],
  );

  const dropdownOptions: DropdownOption[] = [];

  if (comment.isOwner) {
    dropdownOptions.push({
      text: `${translate("Edit")} ${translate("Comment")}`,
      icon: <Edit className="h-4 w-4" />,
      onClick: () => setCommentToEdit(comment),
    });
  }

  if (comment.isOwner) {
    dropdownOptions.push({
      text: `${translate("Delete")} ${translate("Comment")}`,
      icon: <Delete className="h-4 w-4" />,
      color: "error",
      onClick: () => setIsConfirmingDelete(true),
    });
  }

  if (!comment.isOwner && !isCommunityAdmin(comment.commentCreator)) {
    dropdownOptions.push({
      text: translate("Block user"),
      icon: <UserClose className="h-4 w-4" />,
      color: "error",
      onClick: () => setIsConfirmingBlock(true),
    });
  }

  if (!comment.isOwner) {
    dropdownOptions.push({
      text: translate(haveReported ? "Remove Report" : "Report Comment"),
      icon: <Flag className="h-4 w-4" />,
      color: haveReported ? undefined : "error",
      onClick: () => setIsConfirmingReport(true),
    });
  }

  if (dropdownOptions.length === 0) {
    return null;
  }

  return (
    <>
      <Dropdown
        open={dropdownOpen}
        className="min-w-[130px]"
        onOpenChange={handleDropdownOpen}
        contentProps={{ align: "end" }}
        options={dropdownOptions}
        optionsClassName="capitalize"
      >
        <Ellipsis className="text-item-secondary" height={20} width={20} />
      </Dropdown>
      <ConfirmDeleteModal
        commentId={comment.id}
        open={isConfirmingDelete}
        setOpen={setIsConfirmingDelete}
      />
      <ConfirmBlockModal
        userId={comment.commentCreator.userId}
        open={isConfirmingBlock}
        setOpen={setIsConfirmingBlock}
      />
      <ConfirmReportModal
        haveReported={haveReported}
        setHaveReported={setHaveReported}
        comment={comment}
        open={isConfirmingReport}
        setOpen={setIsConfirmingReport}
      />
      <CommunityCommentModal
        open={!!commentToEdit}
        comment={commentToEdit}
        setOpen={(open) => {
          if (!open) {
            setCommentToEdit(undefined);
          }
        }}
      />
    </>
  );
};
